import { createRouter, createWebHistory } from 'vue-router'
import { resolveRoute } from './guard'

export { resolveRoute }

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'dashboard',
      component: () => import('@/views/DashboardView.vue'),
    },
    {
      path: '/ponds/:id',
      name: 'pond-detail',
      component: () => import('@/views/PondDetailView.vue'),
      props: true,
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      // Reachable without a session — the guard skips public routes.
      meta: { public: true },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/ForgotPasswordView.vue'),
      meta: { public: true },
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/views/ResetPasswordView.vue'),
      meta: { public: true },
    },
    {
      path: '/team',
      name: 'team',
      component: () => import('@/views/TeamView.vue'),
      // Admin floor, mirroring the ADMIN floor on every /api/users route.
      // resolveRoute turns this into a redirect to the dashboard rather than a
      // refusal screen — an operator's account is not malfunctioning, this part
      // of the product simply is not theirs.
      meta: { requiresAdmin: true },
    },
    {
      path: '/admin/tenants',
      name: 'admin-tenants',
      component: () => import('@/views/AdminTenantsView.vue'),
      // The PLATFORM axis, mirroring `require_platform_admin` on every
      // /api/admin/* route. Deliberately NOT `requiresAdmin`: that flag asks
      // about rank inside a customer's hierarchy and no rank produces this one
      // — a platform account is created as a `viewer`. resolveRoute keeps the
      // two apart, and a route carrying both metas would be unreachable.
      meta: { requiresPlatformAdmin: true },
    },
    {
      path: '/admin/tenants/:tenantId/audit',
      name: 'admin-audit',
      component: () => import('@/views/AdminAuditView.vue'),
      props: true,
      meta: { requiresPlatformAdmin: true },
    },
    {
      path: '/admin/use-case-profiles',
      name: 'admin-profiles',
      component: () => import('@/views/AdminProfilesView.vue'),
      // The PLATFORM axis, like the two screens either side of it. What this one
      // edits is not any customer's data at all — it is the catalogue of
      // PRODUCTS every tenant reads, so it belongs to us in a stronger sense
      // than the tenant screens do.
      meta: { requiresPlatformAdmin: true },
    },
    {
      path: '/admin/usage',
      name: 'admin-usage',
      component: () => import('@/views/AdminUsageView.vue'),
      // Same axis as the customer screen. `/api/usage` has been platform-only
      // since before this panel existed — usage and cost figures are ours, not
      // any customer's, which is a product decision rather than a rank.
      meta: { requiresPlatformAdmin: true },
    },
    {
      // Private: only a live session reaches it. A user with a pending forced
      // change is confined HERE by resolveRoute (mustChangePassword).
      path: '/change-password',
      name: 'change-password',
      component: () => import('@/views/ChangePasswordView.vue'),
    },
  ],
})

/**
 * Global auth guard for the mock login gate.
 *
 * The session is an HttpOnly cookie, so JS cannot read login state directly.
 * On the first navigation we run a single backend probe (`checkSession`);
 * `bootChecked` makes it run exactly once. The pure redirect decision is
 * delegated to `resolveRoute` once the probe has settled.
 *
 * The store module is imported lazily inside the callback — not at module
 * top level. Two reasons: (1) Pinia is only guaranteed active after `app.use`
 * runs in main.ts; (2) a static `import` here would leave the cycle
 * `client → store → router → client` with a real static edge. With this
 * dynamic import every edge of that cycle is lazy, so the static cycle is
 * genuinely broken.
 */
router.beforeEach(async (to) => {
  const { useAuthStore } = await import('@/stores/auth')
  const authStore = useAuthStore()

  // A full page load is already under way (tenant switch, sign-out): routing
  // inside the page being thrown away would only render the old tenant's
  // stores under the new session. See `resumeLeaving` in the auth store.
  if (authStore.resumeLeaving()) return false

  if (!authStore.bootChecked) {
    await authStore.checkSession()
  }

  return resolveRoute(
    to,
    authStore.isAuthenticated,
    authStore.mustChangePassword,
    // The role arrives with the same session probe above, so by this line it is
    // either known or the user is not authenticated at all.
    authStore.role,
    // Same probe, same guarantee. Passed explicitly rather than read inside
    // `resolveRoute` so that function stays free of the store and testable
    // without one — the reason it lives in its own module at all.
    authStore.isPlatformAdmin,
  )
})

export default router
